import { useFormContext } from 'react-hook-form';
import { Grid2, Typography, Alert } from '@mui/material';
import { FormField, FormFieldType, API_FIELDS_APPLICATION as APPLICATION } from '@enroller/admissions-components';

import { TitledSection } from '../../../layout/TitledSection';
import { yesNoOptions } from '../../../constants/yesNoOptions';
import { HousingFormValues, HOUSING_PAGE_SECTIONS } from './HousingFormValues';

const allergySeverityOptions = [
  { label: 'None', value: 'None' },
  { label: 'Mild', value: 'Mild' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Severe', value: 'Severe' },
];

const glassesAndContactsOptions = [
  { label: 'No', value: 'No' },
  { label: 'Glasses only', value: 'Glasses only' },
  { label: 'Contacts only', value: 'Contacts only' },
  { label: 'Glasses & Contacts', value: 'Glasses & Contacts' },
];

export const AllergiesSection = () => {
  const { control, watch } = useFormContext<HousingFormValues>();

  const section = HOUSING_PAGE_SECTIONS.allergies;
  const allergyOtherAnimalPath = `${section}.allergySeverity.${APPLICATION.allergyOtherAnimal}.value`;
  const allergyOtherAnimalExplainPath = `${section}.allergySeverity.${APPLICATION.allergyOtherAnimal}.${APPLICATION.allergyOtherAnimalExplanation}`;
  const otherAnimalSeverity = watch(allergyOtherAnimalPath);
  const explainOtherAnimalSeverityEnabled = otherAnimalSeverity === 'None';

  return (
    <TitledSection title="Allergies">
      <Grid2 size={12}>
        <Alert variant="outlined" severity="info">
          <Typography variant="body2">
            Please add your defining qualities and interests. This will further help us in finding you an appropriate host family.
          </Typography>
        </Alert>
      </Grid2>

      <Grid2 size={12}>
        <FormField
          control={control}
          name={`${section}.${APPLICATION.allergicToAnimals}`}
          type={FormFieldType.Radio}
          options={yesNoOptions}
          label="Are you allergic to animals with fur?"
        />
      </Grid2>

      <Grid2 size={12}>
        <Typography fontSize="14px">Please select which animals you are allergic and indicate the severity of the allergy:</Typography>
      </Grid2>

      <Grid2 container size={12} columnSpacing={2}>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.allergySeverity.${APPLICATION.allergyDogReaction}`}
            type={FormFieldType.Radio}
            options={allergySeverityOptions}
            label="Dog"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.allergySeverity.${APPLICATION.allergyCatReaction}`}
            type={FormFieldType.Radio}
            options={allergySeverityOptions}
            label="Cat"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.allergySeverity.${APPLICATION.allergyHorseReaction}`}
            type={FormFieldType.Radio}
            options={allergySeverityOptions}
            label="Horse"
          />
        </Grid2>
        <Grid2 size={12} container alignItems="flex-end">
          <FormField control={control} name={allergyOtherAnimalPath} type={FormFieldType.Radio} options={allergySeverityOptions} label="Other" />

          <FormField
            control={control}
            name={allergyOtherAnimalExplainPath}
            type={FormFieldType.Text}
            placeholder="Please explain"
            disabled={explainOtherAnimalSeverityEnabled}
            label=""
          />
        </Grid2>
      </Grid2>

      <Grid2 size={12}>
        <FormField
          control={control}
          name={`${section}.${APPLICATION.nonAllergicDogBreeds}`}
          type={FormFieldType.Text}
          multiline
          rows={6}
          label="Are there any breeds that do not cause an allergic reaction?"
        />
      </Grid2>

      <Grid2 size={12}>
        <Typography fontSize="14px">Can you live with a host family that has:</Typography>
      </Grid2>

      <Grid2 container size={12} columnSpacing={2}>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.dogIndoorsNotInRoom}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="A dog kept in the home but not in your room"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.dogOnlyAtNight}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="A dog that is only in the home at night"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.dogOccasionallyIndoors}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="A dog that is only occasionally in the home"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.dogOutsideOnly}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="A dog kept exclusively outside"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.catIndoorsNotInRoom}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="A cat kept in the home but not in your room"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.catOnlyAtNight}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="A cat that is only in the home at night"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.catOccasionallyIndoors}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="A cat that is only occasionally in the home"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.catOutsideOnly}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="A cat kept exclusively outside"
          />
        </Grid2>
      </Grid2>

      <Grid2 container size={12} columnSpacing={2}>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.allergicToDust}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="Are you allergic to dust?"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.cannotLiveWithCarpet}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="Are you restricted to homes without carpet?"
          />
        </Grid2>
      </Grid2>

      <Grid2 size={12}>
        <FormField
          control={control}
          name={`${section}.${APPLICATION.allergicToFood}`}
          type={FormFieldType.Radio}
          options={yesNoOptions}
          label="Are you allergic to food products?"
        />
      </Grid2>

      <Grid2 size={12}>
        <FormField
          control={control}
          name={`${section}.${APPLICATION.listFoodAllergies}`}
          type={FormFieldType.Text}
          multiline
          rows={6}
          label="List foods you are allergic to (e.g. dairy, nuts, etc.)"
        />
      </Grid2>

      <Grid2 container size={12} columnSpacing={2}>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.specialMeals}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="Do you need special meals?"
          />
        </Grid2>
        <Grid2 size={6}>
          <FormField
            control={control}
            name={`${section}.${APPLICATION.makeSpecialMeals}`}
            type={FormFieldType.Radio}
            options={yesNoOptions}
            label="Can you prepare your own meals?"
          />
        </Grid2>
      </Grid2>

      <Grid2 size={6}>
        <FormField
          control={control}
          name={`${section}.${APPLICATION.allergicToPollen}`}
          type={FormFieldType.Radio}
          options={yesNoOptions}
          label="Are you allergic to pollen?"
        />
      </Grid2>

      <Grid2 size={12}>
        <FormField
          control={control}
          name={`${section}.${APPLICATION.wearsGlassesOrContacts}`}
          type={FormFieldType.Radio}
          options={glassesAndContactsOptions}
          label="Do you wear glasses or contacts?"
        />
      </Grid2>
    </TitledSection>
  );
};
